import { useState, useEffect, useRef } from "react";
import { Button, Form, Input, notification, Upload, Spin, Select } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { get, patch, uploadFile } from "../../../services";
import ReactQuill from "react-quill";
import { ReactQuillOptions } from "../../../utils/react-quill-options";

const EventForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loader, setLoader] = useState(false);
  const [getLoader, setGetLoader] = useState(true);
  const [fileList2, setFileList2] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [information, setInformation] = useState({});
  const [description, setDescription] = useState("");
  const uploadInProgressRef = useRef(false);

  const openNotification = (message, description) => {
    notification.open({ message, description });
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Fetch team member details
  useEffect(() => {
    get(`/team/${id}`)
      .then((response) => {
        const data = response?.data?.member || {};
        setInformation(data);
        setDescription(data.description || "");

        if (data.imageUrl) {
          setFileList2([{ uid: "-1", name: "image.png", url: data.imageUrl }]);
        }

        setGetLoader(false);
      })
      .catch(() => setGetLoader(false));
  }, [id]);

  // Upload component props
  const uploadProps2 = {
    beforeUpload: (file) => {
      const isImage = file.type.startsWith("image/");
      const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB
      const hasSpacesInName = /\s/.test(file.name);

      if (!isImage) {
        openNotification("Error", "Please upload an image file.");
        return false;
      }
      if (!isValidSize) {
        openNotification("Error", "File size exceeds 5MB limit.");
        return false;
      }
      if (hasSpacesInName) {
        openNotification("Error", "Remove spaces from file name before uploading.");
        return false;
      }

      setFileList2([file]);
      return false;
    },
    onRemove: () => setFileList2([]),
    fileList: fileList2,
  };

  // FIXED upload handler
  const uploadFileHandler = async (file) => {
    if (uploadInProgressRef.current) {
      openNotification("Notification", "File is already uploading. Please wait.");
      return null;
    }

    uploadInProgressRef.current = true;

    try {
      // Prepare FormData for the file
      const formData = new FormData();
      formData.append("file", file);

      const response = await uploadFile("/getSignUrlForUpload", formData);

      if (response?.data?.publicUrl) {
        return response.data.publicUrl;
      } else {
        openNotification("Error", "Upload failed — no URL returned.");
        return null;
      }
    } catch (error) {
      openNotification("Error", error?.message || "Something went wrong.");
      return null;
    } finally {
      uploadInProgressRef.current = false;
    }
  };

  const onFinish = async (values) => {
    if (isUploading) {
      openNotification("Notification", "File is already uploading. Please wait.");
      return;
    }

    setIsUploading(true);
    setLoader(true);

    try {
      let uploadedFileUrl = information.imageUrl || "";

      // Upload new image if selected
      if (fileList2.length > 0) {
        const currentFile = fileList2[0];

        if (currentFile.originFileObj) {
          openNotification("Notification", "Uploading file, please wait...");
          const url = await uploadFileHandler(currentFile.originFileObj);
          if (url) uploadedFileUrl = url;
          else throw new Error("File upload failed.");
        } else if (currentFile.url) {
          uploadedFileUrl = currentFile.url;
        }
      } else {
        // If removed manually
        uploadedFileUrl = "";
      }

      const payload = {
        _id: id,
        section: values.section,
        fullName: values.fullName,
        title: values.title || "",
        languagesSpoken: values.languagesSpoken || "",
        description: description || "",
        joiningDate: values.joiningDate || "",
        imageUrl: uploadedFileUrl,
      };

      await patch("/team/bulk-update", [payload]);

      setLoader(false);
      setIsUploading(false);
      openNotification("Success", "Record updated successfully.");
      navigate("/our-team");
    } catch (error) {
      setLoader(false);
      setIsUploading(false);
      openNotification("Error", error?.message || "Something went wrong.");
    }
  };

  return (
    <>
      <div className="header">
        <h3>Edit Team Member</h3>
      </div>

      {getLoader ? (
        <div className="spin_information">
          <Spin />
        </div>
      ) : (
        <div className="form_container">
          <Form
            layout="vertical"
            onFinish={onFinish}
            initialValues={{
              section: information.section || "",
              fullName: information.fullName || "",
              title: information.title || "",
              languagesSpoken: information.languagesSpoken || "",
              joiningDate: formatDateForInput(information.joiningDate),
            }}
            key={JSON.stringify(information)}
          >
            <Form.Item
              name="section"
              label="Section"
              rules={[{ required: true, message: "Please select a section." }]}
            >
              <Select
                placeholder="Select a section"
                allowClear
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                <Select.Option value="Management Team">Management Team</Select.Option>
                <Select.Option value="Sales Team">Sales Team</Select.Option>
                <Select.Option value="Operations Team">Operations Team</Select.Option>
                <Select.Option value="Marketing Team">Marketing Team</Select.Option>
                <Select.Option value="Holiday Homes Team">Holiday Homes Team</Select.Option>
                <Select.Option value="Support Team">Support Team</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item name="imageUrl" label="Profile Image">
              <Upload {...uploadProps2}>
                <Button icon={<UploadOutlined />}>Upload image</Button>
              </Upload>
            </Form.Item>

            <Form.Item
              name="fullName"
              label="Full Name"
              rules={[{ required: true, message: "Please enter the name." }]}
            >
              <Input placeholder="Enter full name" />
            </Form.Item>

            <Form.Item name="title" label="Title">
              <Input placeholder="Enter title" />
            </Form.Item>

            <Form.Item name="languagesSpoken" label="Languages Spoken">
              <Input placeholder="Enter languages" />
            </Form.Item>

            <Form.Item label="Description">
              <ReactQuill
                value={description}
                onChange={setDescription}
                theme="snow"
                modules={ReactQuillOptions}
                placeholder="Write description..."
              />
            </Form.Item>

            <Form.Item name="joiningDate" label="Joining Date">
              <Input type="date" />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loader}>
                Update
              </Button>
            </Form.Item>
          </Form>
        </div>
      )}
    </>
  );
};

export default EventForm;
